import React, { useState } from 'react';
import { Button, Label, Card, CardHeader, CardBody } from '@repo/ui';

interface FolderCreationTestProps {
  onTestFolder: (matterId: string) => Promise<{
    success: boolean;
    matterId: string;
    xtndFolderId?: string;
    testTransactionFolderName?: string;
    error?: string;
  }>;
}

interface ProcessedMatter {
  matterId: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  timestamp: Date;
}

export function FolderCreationTest({ onTestFolder }: FolderCreationTestProps) {
  const [inputText, setInputText] = useState('');
  const [processedMatters, setProcessedMatters] = useState<ProcessedMatter[]>([]);
  const [currentMatter, setCurrentMatter] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };

  const processNextMatter = async (currentProcessedMatters: ProcessedMatter[] = processedMatters) => {
    // Get list of matter IDs from input text
    const matterIds = inputText
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    if (matterIds.length === 0) return;

    // Find the next unprocessed matter
    const nextMatter = matterIds.find(matterId => 
      !currentProcessedMatters.some(pm => pm.matterId === matterId)
    );

    if (!nextMatter) {
      setCurrentMatter(null);
      return;
    }

    setCurrentMatter(nextMatter);

    try {
      const result = await onTestFolder(nextMatter);
      const newProcessedMatter: ProcessedMatter = {
        matterId: nextMatter,
        status: result.success ? 'success' : 'error',
        error: result.success ? undefined : result.error,
        timestamp: new Date()
      };

      setProcessedMatters(prev => {
        const updated = [...prev, newProcessedMatter];
        
        // Check if there are more matters to process
        const remaining = matterIds.filter(matterId => 
          !updated.some(pm => pm.matterId === matterId)
        );

        if (remaining.length > 0) {
          // Continue processing with updated state
          setTimeout(() => processNextMatter(updated), 100);
        } else {
          setCurrentMatter(null);
        }

        return updated;
      });

    } catch (error) {
      const newProcessedMatter: ProcessedMatter = {
        matterId: nextMatter,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date()
      };

      setProcessedMatters(prev => {
        const updated = [...prev, newProcessedMatter];
        
        // Check if there are more matters to process
        const remaining = matterIds.filter(matterId => 
          !updated.some(pm => pm.matterId === matterId)
        );

        if (remaining.length > 0) {
          // Continue processing with updated state
          setTimeout(() => processNextMatter(updated), 100);
        } else {
          setCurrentMatter(null);
        }

        return updated;
      });
    }
  };

  const handleStartProcessing = () => {
    setProcessedMatters([]);
    setCurrentMatter(null);
    processNextMatter([]);
  };

  const handleClear = () => {
    setInputText('');
    setProcessedMatters([]);
    setCurrentMatter(null);
  };

  const getStatusIcon = (status: ProcessedMatter['status']) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'pending':
        return '⏳';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const isProcessing = !!currentMatter;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Input Section */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">Clio Matter IDs</h3>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              ⚠️ Enter Clio <strong>Matter IDs</strong> only (NOT folder IDs)
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="matterIds">Enter Clio Matter IDs (one per line)</Label>
              <textarea
                id="matterIds"
                value={inputText}
                onChange={handleInputChange}
                placeholder="Example: 12345678&#10;98765432&#10;&#10;⚠️ Use Matter IDs from Clio, not folder IDs"
                className="w-full h-40 px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-700 text-xtnd-dark-800 dark:text-xtnd-dark-50 focus:ring-2 focus:ring-xtnd-blue focus:border-transparent"
              />
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={handleStartProcessing}
                disabled={!inputText.trim() || isProcessing}
                className="border-xtnd-blue text-xtnd-blue hover:bg-xtnd-blue hover:text-white"
              >
                {isProcessing ? 'Processing...' : 'Start Processing'}
              </Button>
              <Button
                variant="ghost"
                onClick={handleClear}
                disabled={isProcessing}
                className="text-xtnd-dark-500 hover:text-xtnd-dark-700"
              >
                Clear
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Results Section */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">Processing Results</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {currentMatter && (
                <div className="flex items-center space-x-2 text-sm">
                  <span>⏳</span>
                  <span className="text-xtnd-blue dark:text-xtnd-blue-300">Processing {currentMatter}...</span>
                </div>
              )}
              {processedMatters.map((matter, index) => (
                <div key={`${matter.matterId}-${index}`} className="flex items-start space-x-2 text-sm">
                  <span>{getStatusIcon(matter.status)}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={matter.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}>
                        {matter.matterId}
                      </span>
                      <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300 text-xs">
                        {formatTimestamp(matter.timestamp)}
                      </span>
                    </div>
                    {matter.error && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {matter.error}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {!currentMatter && processedMatters.length === 0 && (
                <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 italic">
                  No matters processed yet
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
